import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  RouterOutlet,
  RouterLink,
  Router,
  NavigationEnd,
  ActivatedRouteSnapshot,
} from '@angular/router';
import {
  MotifHeaderModule,
  MotifThemeModule,
  MotifAvatarModule,
  MotifButtonModule,
  MotifIconModule,
  MotifDropdownModule,
  MotifToastModule,
  MotifModal,
  ModalSizes,
  MotifTooltipModule,
  MotifFormsModule,
} from '@ey-xd/ng-motif';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import {
  IconoirHelpCircle,
  IconoirPrivacyPolicy,
  IconoirBell,
  IconoirCustomMoreHorizontalAlt,
  IconoirMessageAlert,
  IconoirStatsUpSquare,
  IconoirLogOut,
  IconoirSunLight,
  IconoirHalfMoon,
  IconoirSettings,
  IconoirUser,
  MotifCommunicationIcEmail24px,
} from '@ey-xd/motif-icon';
import { UserService } from '@services/user.service';
import { CommonService } from '@services/common.service';
import { SecureStorageService } from '@services/securestorage.service';
import { NotificationService } from '@services/notification.service';
import { ToastMessage } from '@models/toastMessage';
import { PrivacyComponentComponent } from '@shared/privacy-component/privacy-component.component';
import { MsalService } from '@azure/msal-angular';
import { HideShowScreenService } from '@services/hide-show-screen.service';
import { NotificationComponent } from '@components/notification/notification.component';
import { ApplicationConstants } from '@constants/app.constants';
import { IporPrivacyPolicyComponent } from '@shared/ipor-privacy-policy/ipor-privacy-policy.component';

@Component({
  selector: 'app-header',
  imports: [
    RouterOutlet,
    RouterLink,
    MotifHeaderModule,
    TranslatePipe,
    TranslateDirective,
    MotifThemeModule,
    MotifAvatarModule,
    MotifButtonModule,
    MotifToastModule,
    MotifIconModule,
    CommonModule,
    MotifDropdownModule,
    MotifTooltipModule,
    NotificationComponent,
    MotifFormsModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HeaderComponent implements OnInit, OnDestroy {
  theme: string;
  selectedTheme: string;
  btnColor: string = 'primary';
  showMenu: boolean = false;
  user: any;
  userImage: string | null = null;
  @Output() menuToggled: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() themeSelected: EventEmitter<string> = new EventEmitter<string>();
  @Output() projectToggled: EventEmitter<string> = new EventEmitter<string>();
  IconoirLogOut = IconoirLogOut;
  helpCircleIcon = IconoirHelpCircle;
  bellIcon = IconoirBell;
  userMessageIcon = MotifCommunicationIcEmail24px;
  messageIcon = IconoirMessageAlert;
  moreIcon = IconoirCustomMoreHorizontalAlt;
  analyticsIcon = IconoirStatsUpSquare;
  privacyPolicyIcon = IconoirPrivacyPolicy;
  IconoirSettings = IconoirSettings;
  IconoirUser = IconoirUser;
  IconoirSunLight = IconoirSunLight;
  IconoirHalfMoon = IconoirHalfMoon;
  hideMenu: boolean = true;

  toastMessage: ToastMessage = new ToastMessage();
  isPmproLoaded: boolean = true;
  isLanding: boolean = true;

  private readonly validModules = [
    ApplicationConstants.PMPRO,
    ApplicationConstants.IPOR,
  ];

  constructor(
    private readonly userService: UserService,
    private readonly hideShowScreenService: HideShowScreenService,
    public modal: MotifModal,
    private readonly route: Router,
    public commonService: CommonService,
    private readonly authService: MsalService,
    private readonly storageService: SecureStorageService,
    private readonly notificationService: NotificationService,
  ) {
    if (this.storageService.getLocalStorageData('selectedtheme')) {
      this.theme = this.selectedTheme =
        this.storageService.getLocalStorageData('selectedtheme');
      if (this.theme === 'auto') {
        this.selectedTheme = globalThis.matchMedia(
          '(prefers-color-scheme: dark)',
        ).matches
          ? 'dark'
          : 'light';
      }
    }

    if (this.storageService.getSessionStorageData('activeModule')) {
      const activeModule =
        this.storageService.getSessionStorageData('activeModule');
      this.isPmproLoaded = activeModule === 'pmpro';
      this.commonService.isPMproApplicationSelected.next(this.isPmproLoaded);
    } else {
      let module = this.commonService.getActiveModuleFromLocalStorage();
      this.setActiveModule(module);
    }

    this.commonService.toastData.subscribe((res: any) => {
      this.toastMessage = res;
    });
  }

  unreadCount: number = 0;

  ngOnDestroy(): void {
    localStorage.setItem('privacyNotice', 'false');
  }
  ngOnInit(): void {
    this.isLanding = this.route?.url === '/';

    this.route.events.subscribe({
      next: (event) => {
        if (event instanceof NavigationEnd) {
          this.applyModuleFromRouteParams();
          this.isLanding = event.urlAfterRedirects === '/' || event.url === '/';
        }
      },
    });

    this.applyModuleFromRouteParams();

    // menu visibility
    this.hideShowScreenService.menuVisible$.subscribe((visible) => {
      this.hideMenu = visible;
    });

    // theme
    localStorage.setItem('privacyNotice', 'false');
    this.commonService.setTheme(this.selectedTheme);

    // user/profile
    this.userService.user$.subscribe((data) => {
      this.user = data;
      this.commonService.themeChange$.subscribe((theme: string) => {
        this.selectedTheme = this.theme = theme.toLowerCase();
      });
    });

    this.userService.userImage$.subscribe((imageUrl) => {
      this.userImage = imageUrl;
    });

    // notifications
    this.notificationService.unreadCount$.subscribe((count) => {
      this.unreadCount = count;
    });
  }

  showNavMenu() {
    this.hideShowScreenService.showMenu(0);
  }

  private readModuleFromRouteParams(): string | null {
    let routeSnapshot: ActivatedRouteSnapshot | null =
      this.route.routerState.snapshot.root;
    while (routeSnapshot) {
      const module = routeSnapshot.paramMap.get('module');
      if (module) {
        return module.toLowerCase();
      }
      routeSnapshot = routeSnapshot.firstChild;
    }
    return null;
  }

  private applyModuleFromRouteParams(): void {
    const module = this.readModuleFromRouteParams();
    if (!module || !this.validModules.includes(module)) {
      return;
    }
    this.setActiveModule(module);
  }

  private setActiveModule(module: string): void {
    this.storageService.saveSessionStorageData('activeModule', module);
    this.commonService.setActiveModuleinLocalStorage();
    this.isPmproLoaded = module === ApplicationConstants.PMPRO;
    this.commonService.isPMproApplicationSelected.next(this.isPmproLoaded);
  }

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
    this.menuToggled.emit(this.showMenu);
  }

  openNotifications() {
    this.modal.open(NotificationComponent, {
      size: ModalSizes.XL,
      slideout: true,
      disableClose: true,
      minWidth: '25vw',
      maxWidth: '30vw',
      width: '40vw',
    });
  }
  showPrivacyNotice() {
    localStorage.setItem('privacyNotice', 'true');
    if (!this.isPmproLoaded) {
      this.modal.open(IporPrivacyPolicyComponent, {
        size: ModalSizes.XL,
        slideout: false,
        disableClose: true,
        width: '50vw',
      });
      return;
    }

    this.modal.open(PrivacyComponentComponent, {
      size: ModalSizes.XL,
      slideout: false,
      disableClose: true,
      width: '50vw',
    });
  }

  toggleTheme(): void {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    this.commonService.setTheme(this.theme);
    this.themeSelected.emit(this.theme);
  }

  setTheme(theme: string): void {
    this.theme = this.selectedTheme = theme;
    this.commonService.setTheme(theme);
    this.storageService.saveLocalStorageData('selectedtheme', theme);
    if (theme === 'auto') {
      theme = globalThis.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      this.selectedTheme = theme;
    }
    this.themeSelected.emit(theme);
    this.commonService.notifyThemeChange(this.selectedTheme);
  }

  logout(): void {
    let module = this.commonService.getActiveModule();
    this.commonService.stopWatching();
    this.commonService.clearStorage();
    this.storageService.saveLocalStorageData('module', module);
    this.route.navigateByUrl('/logout');
    // Clear MSAL token cache by removing the active account
    this.authService.instance.setActiveAccount(null);
  }

  toggleProject(value: boolean): void {
    const activeModule = value ? 'pmpro' : 'ipor';
    this.isPmproLoaded = value;
    this.commonService.isPMproApplicationSelected.next(value);
    this.storageService.saveSessionStorageData('activeModule', activeModule);
    this.commonService.setActiveModuleinLocalStorage();
    this.projectToggled.emit(activeModule);
  }

  // Listen for clicks on the document
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: any) {
    if (
      event.target.classList.length != 0 &&
      !event.target.classList.contains('side-btn')
    ) {
      const clickedInside = (event.target as HTMLElement).classList.contains(
        'motif-vertical-navigation-show',
      );
      if (!clickedInside) {
        this.showMenu = false;
        this.menuToggled.emit(this.showMenu); // Collapse the sidebar if clicked outside
      }
    }
  }
}
